/* eslint-disable react/no-unknown-property */
import { Canvas, useFrame } from "@react-three/fiber";
import { useRef, useMemo } from "react";
import * as THREE from "three";

function Molecule({ mouse }) {
  const group = useRef();
  const atoms = useMemo(() => {
    const points = [];
    points.push([0, 0, 0]);
    const t = (1 + Math.sqrt(5)) / 2;
    const raw = [
      [-1,  t,  0], [ 1,  t,  0], [-1, -t,  0], [ 1, -t,  0],
      [ 0, -1,  t], [ 0,  1,  t], [ 0, -1, -t], [ 0,  1, -t],
      [ t,  0, -1], [ t,  0,  1], [-t,  0, -1], [-t,  0,  1],
    ];
    raw.forEach((p) => points.push(p.map((v) => v * 1.4)));
    return points;
  }, []);
  const bonds = useMemo(() => atoms.slice(1).map((p) => [[0, 0, 0], p]), [atoms]);

  useFrame((state, delta) => {
    if (!group.current) return;
    group.current.rotation.y += delta * 0.22;
    group.current.rotation.x += delta * 0.08;
    const mx = mouse.current?.x || 0;
    const my = mouse.current?.y || 0;
    group.current.position.x = mx * 0.4;
    group.current.position.y = -my * 0.3;
  });

  return (
    <group ref={group}>
      {atoms.map((p, i) => (
        <mesh key={i} position={p}>
          <sphereGeometry args={[i === 0 ? 0.42 : 0.24, 48, 48]} />
          <meshPhysicalMaterial
            color={i === 0 ? "#0a0a0a" : "#1a1a1a"}
            metalness={0.85}
            roughness={0.15}
            clearcoat={0.9}
            clearcoatRoughness={0.1}
            reflectivity={1}
          />
        </mesh>
      ))}
      {bonds.map(([a, b], i) => {
        const start = new THREE.Vector3(...a);
        const end = new THREE.Vector3(...b);
        const dir = new THREE.Vector3().subVectors(end, start);
        const len = dir.length();
        const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
        const q = new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          dir.clone().normalize()
        );
        return (
          <mesh key={i} position={mid.toArray()} quaternion={q}>
            <cylinderGeometry args={[0.028, 0.028, len, 16]} />
            <meshStandardMaterial color="#333" metalness={0.7} roughness={0.35} />
          </mesh>
        );
      })}
    </group>
  );
}

// Orbiting glow ring particles
function OrbitParticles() {
  const points = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(200 * 3);
    for (let i = 0; i < 200; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 3.2 + Math.random() * 0.8;
      const y = (Math.random() - 0.5) * 0.4;
      arr[i * 3] = Math.cos(a) * r;
      arr[i * 3 + 1] = y;
      arr[i * 3 + 2] = Math.sin(a) * r;
    }
    return arr;
  }, []);
  useFrame((state, delta) => {
    if (points.current) points.current.rotation.y += delta * 0.15;
  });
  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={200} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial color="#00529B" size={0.05} transparent opacity={0.55} sizeAttenuation />
    </points>
  );
}

function Scene({ mouse }) {
  return (
    <>
      <ambientLight intensity={0.35} />
      <directionalLight position={[5, 5, 5]} intensity={1.2} />
      <directionalLight position={[-5, -3, -5]} intensity={0.7} color="#00529B" />
      <pointLight position={[0, 0, 5]} intensity={1.5} color="#ffffff" />
      <pointLight position={[3, 3, -3]} intensity={0.8} color="#5A2A82" />
      <pointLight position={[-3, -3, -3]} intensity={0.6} color="#007A78" />
      <OrbitParticles />
      <Molecule mouse={mouse} />
    </>
  );
}

export default function MoleculeCanvas({ mouseRef }) {
  return (
    <Canvas
      camera={{ position: [0, 0, 7], fov: 45 }}
      dpr={[1, 1.8]}
      style={{ background: "transparent" }}
      gl={{ antialias: true, alpha: true }}
    >
      <Scene mouse={mouseRef} />
    </Canvas>
  );
}
